import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import ConfirmUnpublishModal from './ConfirmUnpublishModal';
import ConfirmPushChangesModal from './ConfirmPushChangesModal';
import ConfirmDiscardChangesModal from './ConfirmDiscardChangesModal';
import ErrorModal from './ErrorModal';
import RequestingModal from './RequestingModal';

const PHASES = {
  idle: 'idle',
  publishing: 'publishing',
  unpublishing: 'unpublishing',
  discarding: 'discarding',
  error: 'error',
  confirm: 'confirm',
  confirmUnpublish: 'confirm-unpublish',
  confirmDiscardChanges: 'confirm-discard-changes',
};

/**
 * Ensures a minimum duration floor for fast-resolving
 * promises that have loading states
 *
 * @param promises
 * @returns {Promise<Awaited<unknown>[]>}
 */

const delayPromise = (...promises) =>
  Promise.all([
    ...promises,
    new Promise(res => {
      setTimeout(res, 1000);
    }),
  ]);

export function PublishingManager({
  name,
  published,
  hasChanges,
  publishedAt,
  canPublish = true,
  renderConfirmPushChangesModal = ConfirmPushChangesModal,
  onPublish,
  onUnpublish,
  onDiscardChanges,
  children,
}) {
  const [{ publishingPhase, prevPublishingPhase }, setPublishingStatus] =
    useState({
      publishingPhase: PHASES.idle,
      prevPublishingPhase: null,
    });

  /**
   * since modals stay mounted as they transition out, we must base any language
   * they contain not on the current state, but the previous one
   */

  const verb = useMemo(() => {
    switch (prevPublishingPhase) {
      case PHASES.confirm:
      case PHASES.publishing:
        return 'Publishing';
      case PHASES.confirmUnpublish:
      case PHASES.unpublishing:
        return 'Unpublishing';
      case PHASES.confirmDiscardChanges:
      case PHASES.discarding:
        return 'Discarding changes to';
      default:
        return null;
    }
  }, [prevPublishingPhase]);

  const setPublishingPhase = nextPhase =>
    setPublishingStatus(prevPhase => ({
      prevPublishingPhase: prevPhase.publishingPhase,
      publishingPhase: nextPhase,
    }));

  const handleConfirmPublish = () => {
    setPublishingPhase(PHASES.confirm);
  };

  const handleConfirmUnpublish = () => {
    setPublishingPhase(PHASES.confirmUnpublish);
  };
  const handleConfirmDiscardChanges = () => {
    setPublishingPhase(PHASES.confirmDiscardChanges);
  };

  const handlePublish = () => {
    setPublishingPhase(PHASES.publishing);
    delayPromise(onPublish())
      ?.then(() => {
        setPublishingPhase(PHASES.idle);
      })
      .catch(() => {
        setPublishingPhase(PHASES.error);
      });
  };

  const handleClose = () => setPublishingPhase(PHASES.idle);

  const handleUnpublish = () => {
    setPublishingPhase(PHASES.unpublishing);
    delayPromise(onUnpublish())
      .then(() => {
        setPublishingPhase(PHASES.idle);
      })
      .catch(() => {
        setPublishingPhase(PHASES.error);
      });
  };

  const handleDiscardChanges = () => {
    setPublishingPhase(PHASES.discarding);
    delayPromise(onDiscardChanges(publishedAt))
      .then(() => {
        setPublishingPhase(PHASES.idle);
      })
      .catch(() => {
        setPublishingPhase(PHASES.error);
      });
  };

  const disabled = !canPublish;

  return (
    <>
      {children({
        disabled,
        hasChanges,
        onConfirmPublish: handleConfirmPublish,
        onConfirmDiscardChanges: handleConfirmDiscardChanges,
        onConfirmUnpublish: handleConfirmUnpublish,
        published,
      })}
      <ConfirmUnpublishModal
        onUnpublish={handleUnpublish}
        onClose={handleClose}
        visible={publishingPhase === PHASES.confirmUnpublish}
      />
      {renderConfirmPushChangesModal({
        onPublish: handlePublish,
        onClose: handleClose,
        visible: publishingPhase === PHASES.confirm,
      })}
      <ConfirmDiscardChangesModal
        onDiscard={handleDiscardChanges}
        onClose={handleClose}
        visible={publishingPhase === PHASES.confirmDiscardChanges}
      />
      <RequestingModal
        content={`${verb} ${name}`}
        visible={[
          PHASES.unpublishing,
          PHASES.publishing,
          PHASES.discarding,
        ].includes(publishingPhase)}
      />
      <ErrorModal
        visible={publishingPhase === PHASES.error}
        onClose={handleClose}
      />
    </>
  );
}

export const PublishingManagerPropTypes = {
  name: PropTypes.string,
  published: PropTypes.bool,
  hasChanges: PropTypes.bool,
  publishedAt: PropTypes.number,
  canPublish: PropTypes.bool,
  renderConfirmPushChangesModal: PropTypes.func,
  onPublish: PropTypes.func,
  onUnpublish: PropTypes.func,
  onDiscardChanges: PropTypes.func,
  children: PropTypes.func,
};

PublishingManager.propTypes = PublishingManagerPropTypes;

export default PublishingManager;
